// booking-actions.service.ts
import { Injectable, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { finalize, forkJoin, of } from "rxjs";

import { BookingStore } from "./booking.store";
import { ReservationService } from "src/app/service/reservation.service";
import { ReservationConflictService } from "src/app/service/reservation-conflict.service";
import { AuthService } from "src/app/service/auth.service";

import { RowType, Reservation, ApiReservation } from "src/app/models";
import { toISO, minutesToLabel } from "src/app/utils/time.utils";

import {
  BookingDialogComponent,
  BookingDialogData,
} from "../detail/booking-detail.component";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from "../confirm-dialog/confirm-dialog.component";
import { getPairIds } from "./pairing.engine";
import {
  buildFlipUpdatesPayload,
  buildUpdateBody,
} from "./dialog-payloads.engine";
import { Flip, reflowCascadeStable } from "./reflow.engine";

@Injectable({ providedIn: "root" })
export class BookingActionsService {
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(BookingStore);
  private readonly reservationService = inject(ReservationService);
  private readonly conflictService = inject(ReservationConflictService);
  private readonly authService = inject(AuthService);

  private confirm$(data: ConfirmDialogData) {
    return this.dialog
      .open(ConfirmDialogComponent, { data, width: "420px" })
      .afterClosed();
  }

  /** Flow édition (ouvre le dialog, persiste, reflow, notif) */
  editReservationFlow(input: {
    res: Reservation;
    dialogData: BookingDialogData; // <-- on passe le payload prêt à l’emploi
    decideSecondForRow: (
      rowType: RowType,
      rowId: string,
      startMin: number,
      endMin: number,
      excludeId?: string
    ) => boolean;
    reflowCascadeStable: (
      activeRowType: RowType,
      activeRowId: string,
      preferId?: string
    ) => Array<{
      id: string;
      rowType: RowType;
      rowId: string;
      newFlag: boolean;
    }>;
    onRollback: (
      prev: Reservation[],
      prevAircraftId?: string,
      prevInstructorId?: string
    ) => void;
    refreshView: (reason: string) => void;
  }) {
    const r = input.res;

    const ref = this.dialog.open(BookingDialogComponent, {
      width: "auto",
      height: "auto",
      panelClass: "full-screen-dialog",
      data: input.dialogData,
    });

    ref.afterClosed().subscribe((result) => {
      if (result?.action !== "save") return;

      const nextAircraftId =
        result.aircraftId ??
        (result.rowType === "aircraft" ? result.rowId : undefined);
      const nextInstructorId =
        result.instructorId ??
        (result.rowType === "instructor" ? result.rowId : undefined);
      const nextStudentId: string | null = result.studentId ?? null;

      const startMin = result.startMin;
      const endMin = result.endMin;

      const secondAircraft = nextAircraftId
        ? input.decideSecondForRow(
            "aircraft",
            String(nextAircraftId),
            startMin,
            endMin,
            r.id
          )
        : false;
      const secondInstructor = nextInstructorId
        ? input.decideSecondForRow(
            "instructor",
            String(nextInstructorId),
            startMin,
            endMin,
            r.id
          )
        : false;

      this.confirm$({
        title: "Edit booking",
        message: `Confirm ${minutesToLabel(startMin)} → ${minutesToLabel(
          endMin
        )} ?`,
        confirmText: "Save",
      }).subscribe((ok) => {
        if (!ok) return;

        const before = this.store.reservations();
        let next = [...before];

        const upsert = (
          rowType: RowType,
          rowId: string,
          sec: boolean,
          isSolo: boolean
        ) => {
          const idx = next.findIndex(
            (x) => x.id === r.id && x.rowType === rowType
          );
          if (idx >= 0)
            next[idx] = {
              ...next[idx],
              rowId,
              startMin,
              endMin,
              isSolo,
              secondPosition: sec,
            };
          else
            next.push({
              id: r.id,
              rowType,
              rowId,
              startMin,
              endMin,
              isSolo,
              secondPosition: sec,
            } as any);
        };
        const removeByType = (rowType: RowType) => {
          next = next.filter((x) => !(x.id === r.id && x.rowType === rowType));
        };

        if (nextAircraftId)
          upsert(
            "aircraft",
            String(nextAircraftId),
            secondAircraft,
            !!result.isSolo
          );
        else removeByType("aircraft");

        if (nextInstructorId)
          upsert(
            "instructor",
            String(nextInstructorId),
            secondInstructor,
            !!result.isSolo
          );
        else removeByType("instructor");

        this.store.setReservations(next);

        const primaryType: RowType =
          nextAircraftId !== undefined ? "aircraft" : "instructor";
        const primaryRowId =
          primaryType === "aircraft"
            ? String(nextAircraftId)
            : String(nextInstructorId);

        const flips = input.reflowCascadeStable(
          primaryType,
          primaryRowId!,
          r.id
        );

        const startISO = toISO(this.store.currentDate(), startMin);
        const endISO = toISO(this.store.currentDate(), endMin);

        const body = buildUpdateBody({
          rowType: primaryType,
          startISO,
          endISO,
          // Les deux flags calculés ci-dessus :
          secondActive:
            primaryType === "aircraft" ? secondAircraft : secondInstructor,
          secondOther:
            primaryType === "aircraft" ? secondInstructor : secondAircraft,
          aircraftId: nextAircraftId ?? null,
          instructorId: nextInstructorId ?? null,
          studentId: nextStudentId,
        });

        this.reservationService.updateReservation(r.id, body).subscribe({
          next: (_updated: ApiReservation) => {
            const updates = buildFlipUpdatesPayload(flips, r.id).map(
              ({ id, payload }) =>
                this.reservationService.updateReservation(id, payload)
            );
            (updates.length ? forkJoin(updates) : of(null))
              .pipe(finalize(() => input.refreshView("edit")))
              .subscribe();

            const { instructorId } = getPairIds(
              this.store.reservations(),
              r.id
            );
            const instructor = this.store
              .instructors()
              .find((i) => i.id === instructorId);
            this.conflictService.handlePostSaveNotification({
              instructor,
              currentDate: this.store.currentDate(),
              startMin,
              endMin,
              openHour: this.store.open,
              closeHour: this.store.close,
              creatorUser: this.authService.currentUser ?? null,
            });
          },
          error: (err) => {
            input.onRollback(
              before,
              input.dialogData.aircraftId,
              input.dialogData.instructorId
            );
            // alert(err?.error?.error ?? "Erreur lors de la modification");
          },
        });
      });
    });
  }

  /** Flow création (optimiste) */
  createFromDialogFlow(
    result: any,
    decideSecondForRow: (
      rowType: RowType,
      rowId: string,
      startMin: number,
      endMin: number,
      excludeId?: string
    ) => boolean,
    reflowCascadeStable: (
      activeRowType: RowType,
      activeRowId: string,
      preferId?: string
    ) => Array<{
      id: string;
      rowType: RowType;
      rowId: string;
      newFlag: boolean;
    }>,
    refreshView: (reason: string) => void
  ) {
    const aircraftId =
      result.aircraftId ??
      (result.rowType === "aircraft" ? result.rowId : null);
    const instructorId =
      result.instructorId ??
      (result.rowType === "instructor" ? result.rowId : null);
    const studentId: string | null = result.studentId ?? null;
    if (!aircraftId && !instructorId) return;

    const startMin = result.startMin;
    const endMin = result.endMin;

    const primaryRowType: RowType =
      aircraftId && result.rowType === "aircraft"
        ? "aircraft"
        : instructorId && result.rowType === "instructor"
        ? "instructor"
        : aircraftId
        ? "aircraft"
        : "instructor";

    const primaryRowId =
      primaryRowType === "aircraft" ? String(aircraftId) : String(instructorId);

    const secondAircraftAtCreate =
      aircraftId != null
        ? decideSecondForRow(
            "aircraft",
            String(aircraftId),
            startMin,
            endMin,
            undefined
          )
        : undefined;

    const secondInstructorAtCreate =
      instructorId != null
        ? decideSecondForRow(
            "instructor",
            String(instructorId),
            startMin,
            endMin,
            undefined
          )
        : undefined;

    this.confirm$({
      title: "Create booking",
      message: `Confirm ${minutesToLabel(startMin)} → ${minutesToLabel(
        endMin
      )} ?`,
      confirmText: "Save",
    }).subscribe((ok) => {
      if (!ok) return;

      const groupId = "grp_" + Math.random().toString(36).slice(2);
      const tempId = "tmp_" + Math.random().toString(36).slice(2);

      const optimistic: Reservation[] = [
        {
          id: tempId,
          rowType: primaryRowType,
          rowId: primaryRowId!,
          startMin,
          endMin,
          secondPosition:
            primaryRowType === "aircraft"
              ? !!secondAircraftAtCreate
              : !!secondInstructorAtCreate,
          groupId,
        } as any,
      ];

      const hasBoth = !!aircraftId && !!instructorId;
      if (hasBoth) {
        const mirrorRowType: RowType =
          primaryRowType === "aircraft" ? "instructor" : "aircraft";
        const mirrorRowId =
          mirrorRowType === "aircraft"
            ? String(aircraftId)
            : String(instructorId);
        const secondMirror =
          mirrorRowType === "aircraft"
            ? !!secondAircraftAtCreate
            : !!secondInstructorAtCreate;
        optimistic.push({
          id: tempId,
          rowType: mirrorRowType,
          rowId: mirrorRowId!,
          startMin,
          endMin,
          secondPosition: secondMirror,
          groupId,
        } as any);
      }

      this.store.setReservations([...this.store.reservations(), ...optimistic]);

      const startISO = toISO(this.store.currentDate(), startMin);
      const endISO = toISO(this.store.currentDate(), endMin);

      this.reservationService
        .createReservation({
          aircraftId: aircraftId ?? null,
          instructorId: instructorId ?? null,
          studentId: studentId ?? null,
          notes: result.notes,
          start: startISO,
          end: endISO,
          status: "CONFIRMED" as const,
          ...(secondAircraftAtCreate !== undefined
            ? { secondPositionAircraft: secondAircraftAtCreate }
            : {}),
          ...(secondInstructorAtCreate !== undefined
            ? { secondPositionInstructor: secondInstructorAtCreate }
            : {}),
        })
        .subscribe({
          next: (created: ApiReservation) => {
            this.store.setReservations(
              this.store
                .reservations()
                .map((r) =>
                  (r as any).groupId === groupId
                    ? ({ ...r, id: String(created.id) } as any)
                    : r
                )
            );

            const flips = reflowCascadeStable(
              primaryRowType,
              primaryRowId!,
              String(created.id)
            );
            const updates = buildFlipUpdatesPayload(
              flips,
              String(created.id)
            ).map(({ id, payload }) =>
              this.reservationService.updateReservation(id, payload)
            );
            (updates.length ? forkJoin(updates) : of(null))
              .pipe(finalize(() => refreshView("create")))
              .subscribe();

            const instructor = this.store
              .instructors()
              .find(
                (i) =>
                  i.id ===
                  (result.instructorId ??
                    (result.rowType === "instructor" ? result.rowId : null))
              );
            const aircraftCode = result.aircraftId
              ? this.store.aircrafts().find((a) => a.id === result.aircraftId)
                  ?.code
              : undefined;

            this.conflictService.handlePostSaveNotification({
              instructor,
              currentDate: this.store.currentDate(),
              startMin,
              endMin,
              openHour: this.store.open,
              closeHour: this.store.close,
              creatorUser: this.authService.currentUser ?? null,
              aircraftCode,
            });
          },
          error: (err) => {
            this.store.setReservations(
              this.store
                .reservations()
                .filter((r) => (r as any).groupId !== groupId)
            );
            // alert(err?.error?.error ?? "Erreur lors de la création");
          },
        });
    });
  }

  /** Flow move/resize (confirm + persist + reflow + notif) */
  confirmAndPersistMoveOrResizeFlow(input: {
    id: string;
    rowType: RowType;
    startMin: number;
    endMin: number;
    actionLabel: string;
    confirmBtn: string;
    reason: "move" | "resize";
    decideSecondForRow: (
      rowType: RowType,
      rowId: string,
      startMin: number,
      endMin: number,
      excludeId?: string
    ) => boolean;
    setSecondForBothLines: (
      resId: string,
      activeType: RowType,
      secondActive: boolean,
      secondOther?: boolean
    ) => void;
    clearPreview: (id?: string) => void;
    reflowCascadeStable: (
      activeRowType: RowType,
      activeRowId: string,
      preferId?: string
    ) => Array<{
      id: string;
      rowType: RowType;
      rowId: string;
      newFlag: boolean;
    }>;
    onRollback: () => void;
    refreshView: (reason: string) => void;
  }) {
    const { id, rowType, startMin, endMin, actionLabel, confirmBtn } = input;

    this.confirm$({
      title: actionLabel,
      message: `Confirm ${minutesToLabel(startMin)}–${minutesToLabel(
        endMin
      )} ?`,
      confirmText: confirmBtn,
    }).subscribe((ok) => {
      if (!ok) {
        input.onRollback();
        return;
      }

      const { aircraftId, instructorId } = getPairIds(
        this.store.reservations(),
        id
      );

      const secondActive = input.decideSecondForRow(
        rowType,
        rowType === "aircraft" ? String(aircraftId) : String(instructorId),
        startMin,
        endMin,
        id
      );

      const otherType: RowType =
        rowType === "aircraft" ? "instructor" : "aircraft";
      const otherId = otherType === "aircraft" ? aircraftId : instructorId;

      let secondOther: boolean | undefined = undefined;
      if (otherId) {
        secondOther = input.decideSecondForRow(
          otherType,
          String(otherId),
          startMin,
          endMin,
          id
        );
      }

      input.setSecondForBothLines(id, rowType, secondActive, secondOther);
      input.clearPreview(id);

      const activeRowId = this.store
        .reservations()
        .find((x) => x.id === id && x.rowType === rowType)?.rowId;
      const flips = activeRowId
        ? input.reflowCascadeStable(rowType, String(activeRowId), id)
        : [];

      const body = buildUpdateBody({
        rowType,
        startISO: toISO(this.store.currentDate(), startMin),
        endISO: toISO(this.store.currentDate(), endMin),
        secondActive,
        secondOther,
      });

      this.reservationService.updateReservation(id, body).subscribe({
        next: (_updated: ApiReservation) => {
          const updates = buildFlipUpdatesPayload(flips, id).map(
            ({ id, payload }) =>
              this.reservationService.updateReservation(id, payload)
          );
          (updates.length ? forkJoin(updates) : of(null))
            .pipe(finalize(() => input.refreshView(input.reason)))
            .subscribe();

          const { instructorId } = getPairIds(this.store.reservations(), id);
          const instructor = this.store
            .instructors()
            .find((i) => i.id === instructorId);
          this.conflictService.handlePostSaveNotification({
            instructor,
            currentDate: this.store.currentDate(),
            startMin,
            endMin,
            openHour: this.store.open,
            closeHour: this.store.close,
            creatorUser: this.authService.currentUser ?? null,
          });
        },
        error: (_err) => {
          input.onRollback();
          // alert("Erreur lors de la mise à jour");
        },
      });
    });
  }

  /** Flow suppression : supprime, reflow des lignes impactées, persiste les flips */
  deleteReservationFlow(input: {
    resId: string;
    refreshView: (reason: string) => void;
  }) {
    const { resId, refreshView } = input;

    const before = this.store.reservations();

    // On récupère les ids pair (avion/instructeur) AVANT la suppression
    const { aircraftId, instructorId } = getPairIds(before, resId);

    // 1) Optimiste : on enlève localement toutes les entrées de ce group id
    const afterRemove = before.filter((r) => r.id !== resId);
    this.store.setReservations(afterRemove);

    // 2) Reflow cascade des lignes potentiellement impactées
    let acc = this.store.reservations();
    const flipsAll: Flip[] = [];

    if (aircraftId) {
      const t = reflowCascadeStable(acc, "aircraft", String(aircraftId));
      acc = t.next;
      flipsAll.push(...t.flips);
    }
    if (instructorId) {
      const t = reflowCascadeStable(acc, "instructor", String(instructorId));
      acc = t.next;
      flipsAll.push(...t.flips);
    }

    // Applique le résultat du reflow à l’UI
    this.store.setReservations(acc);

    // 3) Appel API suppression, puis persistence des flips (secondPosition*)
    this.reservationService.deleteReservation(resId).subscribe({
      next: () => {
        const updates = buildFlipUpdatesPayload(flipsAll, resId).map(
          ({ id, payload }) =>
            this.reservationService.updateReservation(id, payload)
        );
        (updates.length ? forkJoin(updates) : of(null))
          .pipe(finalize(() => refreshView("delete")))
          .subscribe();
      },
      error: (_) => {
        // rollback UI si échec
        this.store.setReservations(before);
      },
    });
  }
}
